import { User } from 'lucide-react';
import { useEndUserDetail } from '../hooks/useEndUserDetail';
import { authProviderLabelMap, statusLabelMap, statusVariantMap } from '../utils/end-users.utils';
import { Field } from './Field';
import { ProviderIcon } from './ProviderIcon';
import { ModalForm } from '@/shared/components/ui/modal-form';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { formatAdminDateTime } from '@/shared/utils/format-date.utils';
import { getRoleLabel } from '@/shared/utils/role-label.utils';

interface EndUserDetailModalProps {
  userId: string | null;
  onClose: () => void;
}

export function EndUserDetailModal({ userId, onClose }: EndUserDetailModalProps) {
  const { data: detail, isLoading, isError } = useEndUserDetail(userId);

  return (
    <ModalForm
      open={!!userId}
      onOpenChange={onClose}
      headerIcon={<User />}
      title="Detalle del cliente"
      description="Información detallada del cliente seleccionado."
      className="sm:max-w-140"
      showFooter={false}
    >
      {isLoading && <div className="py-8" aria-hidden />}

      {isError && (
        <p className="text-sm text-negative py-4">Error al cargar el detalle del cliente.</p>
      )}

      {detail && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nombre">{detail.displayName ?? '—'}</Field>
            <Field label="Correo">{detail.email}</Field>
            <Field label="Estado de cuenta">
              <StatusBadge
                status={statusVariantMap[detail.status]}
                label={statusLabelMap[detail.status]}
              />
            </Field>
            <Field label="Proveedor de autenticación">
              {detail.authProviders.length === 0 ? (
                <span className="opacity-50">—</span>
              ) : (
                <div className="flex flex-row flex-wrap gap-2 mt-0.5">
                  {detail.authProviders.map((p) => (
                    <Tooltip key={p.provider}>
                      <TooltipTrigger asChild>
                        <div className="flex items-center justify-center rounded-md border border-outline px-2.5 py-1.5 cursor-default">
                          <ProviderIcon provider={p.provider} />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <div className="flex flex-col gap-0.5 text-center">
                          <span>{authProviderLabelMap[p.provider] ?? p.provider}</span>
                          <span className="opacity-70 text-xs">
                            {formatAdminDateTime(p.connectedAt)}
                          </span>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              )}
            </Field>
            <Field label="Fecha de registro">{formatAdminDateTime(detail.createdAt)}</Field>
            <Field label="Último acceso">{formatAdminDateTime(detail.lastLoginAt)}</Field>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs text-primary/60 uppercase tracking-wide">
              Espacios de trabajo
            </span>
            {detail.workspaces.length === 0 ? (
              <p className="text-primary text-sm opacity-50">Sin espacios de trabajo</p>
            ) : (
              <div className="flex flex-col gap-2">
                {detail.workspaces.map((ws) => (
                  <div
                    key={ws.tenantId}
                    className="flex items-center justify-between rounded-lg border border-outline px-3 py-2"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-light text-sm font-medium">{ws.tenantName}</span>
                      <span className="text-primary text-xs opacity-60">{ws.tenantSlug}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-primary text-xs capitalize">
                        {getRoleLabel(ws.role)}
                      </span>
                      <StatusBadge
                        status={ws.isActive ? 'active' : 'inactive'}
                        label={ws.isActive ? 'Activo' : 'Inactivo'}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <UnifiedButton variant="outline" onClick={onClose}>
              Cerrar
            </UnifiedButton>
          </div>
        </div>
      )}
    </ModalForm>
  );
}
